import { Component, OnDestroy, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { BackToTopComponent } from '@app/common/components/ui/back-to-top/back-to-top';
import { Subscription } from 'rxjs';
import { SiteSettingService } from '@app/services/content/site-settings/site-setting.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, BackToTopComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class App implements OnInit, OnDestroy {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private routerEventsSub?: Subscription;
  private hideLoaderTimeout?: ReturnType<typeof setTimeout>;
  private loaderFailsafeTimeout?: ReturnType<typeof setTimeout>;
  private imageMutationObserver?: MutationObserver;
  private imageOptimizeRafId?: number;
  isRouteLoading = signal(false);
  protected readonly title = signal('frontend');

  constructor(
    private router: Router,
    private siteSettingService: SiteSettingService,
  ) {}

  ngOnInit(): void {
    this.siteSettingService.warmPublicSettingsCache();
    this.scheduleImageOptimization();
    this.startImageMutationObserver();

    this.routerEventsSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (this.hideLoaderTimeout) {
          clearTimeout(this.hideLoaderTimeout);
          this.hideLoaderTimeout = undefined;
        }
        if (this.loaderFailsafeTimeout) {
          clearTimeout(this.loaderFailsafeTimeout);
          this.loaderFailsafeTimeout = undefined;
        }
        this.isRouteLoading.set(true);

        // Safety net: never let the overlay block the UI indefinitely.
        this.loaderFailsafeTimeout = setTimeout(() => {
          this.isRouteLoading.set(false);
          this.loaderFailsafeTimeout = undefined;
        }, 4000);
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError ||
        event instanceof NavigationSkipped
      ) {
        this.scheduleImageOptimization();

        // Slight delay makes transition feel smoother and avoids flicker.
        this.hideLoaderTimeout = setTimeout(() => {
          this.isRouteLoading.set(false);
          this.hideLoaderTimeout = undefined;
        }, 120);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.imageMutationObserver) {
      this.imageMutationObserver.disconnect();
      this.imageMutationObserver = undefined;
    }
    if (this.isBrowser && this.imageOptimizeRafId !== undefined) {
      cancelAnimationFrame(this.imageOptimizeRafId);
      this.imageOptimizeRafId = undefined;
    }
    if (this.hideLoaderTimeout) {
      clearTimeout(this.hideLoaderTimeout);
    }
    if (this.loaderFailsafeTimeout) {
      clearTimeout(this.loaderFailsafeTimeout);
    }
    this.routerEventsSub?.unsubscribe();
  }

  private startImageMutationObserver(): void {
    if (!this.isBrowser || this.imageMutationObserver) return;

    this.imageMutationObserver = new MutationObserver(() => {
      this.scheduleImageOptimization();
    });

    this.imageMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset'],
    });
  }

  private scheduleImageOptimization(): void {
    if (!this.isBrowser || this.imageOptimizeRafId !== undefined) return;

    this.imageOptimizeRafId = requestAnimationFrame(() => {
      this.imageOptimizeRafId = undefined;
      this.optimizeAllImages();
    });
  }

  private optimizeAllImages(): void {
    const images = document.querySelectorAll('img');
    let eagerBudget = 2;

    for (const image of images) {
      const img = image as HTMLImageElement;

      if (!img.getAttribute('decoding')) {
        img.setAttribute('decoding', 'async');
      }

      if (!img.getAttribute('loading')) {
        const eager = eagerBudget > 0 && this.isLikelyAboveFold(img);
        img.setAttribute('loading', eager ? 'eager' : 'lazy');
        if (eager) eagerBudget -= 1;
      }

      if (!img.getAttribute('fetchpriority')) {
        const isEager = img.getAttribute('loading') === 'eager';
        img.setAttribute('fetchpriority', isEager ? 'high' : 'low');
      }
    }
  }

  private isLikelyAboveFold(img: HTMLImageElement): boolean {
    const rect = img.getBoundingClientRect();
    return rect.top < window.innerHeight * 1.2 && rect.bottom > 0 && rect.width > 0;
  }
}
